import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import type { PageResponse, RepertoireEntry } from '@/lib/types';

const REPERTOIRE_PAGE_SIZE = 50;

type RepertoireParams = {
  page?: number;
  size?: number;
  search?: string;
  queueStatusId?: string | null;
  masteryLevelId?: string | null;
  sort?: string | null;
  direction?: string | null;
};

export function useRepertoireEntry(
  arrangementId: string,
): ReturnType<typeof useQuery<RepertoireEntry>> {
  return useQuery({
    queryKey: ['repertoire', 'entry', arrangementId],
    queryFn: () => api.fetch<RepertoireEntry>(`/repertoire/${arrangementId}`),
    enabled: Boolean(arrangementId),
  });
}

export function useRepertoire(params: RepertoireParams = {}): ReturnType<typeof useQuery<PageResponse<RepertoireEntry>>> {
  const { page = 0, size = 50, search, queueStatusId, masteryLevelId, sort, direction } = params;
  return useQuery({
    queryKey: ['repertoire', { page, size, search, queueStatusId, masteryLevelId, sort, direction }],
    queryFn: () => {
      const searchParams = new URLSearchParams();
      searchParams.set('page', String(page));
      searchParams.set('size', String(size));
      if (search) searchParams.set('search', search);
      if (queueStatusId) searchParams.set('queueStatusId', queueStatusId);
      if (masteryLevelId) searchParams.set('masteryLevelId', masteryLevelId);
      if (sort) searchParams.set('sort', sort);
      if (direction) searchParams.set('direction', direction);
      return api.fetch<PageResponse<RepertoireEntry>>(`/repertoire?${searchParams}`);
    },
  });
}

type RepertoireInfiniteParams = Omit<RepertoireParams, 'page'>;

export function useRepertoireInfinite(params: RepertoireInfiniteParams = {}): ReturnType<
  typeof useInfiniteQuery<
    PageResponse<RepertoireEntry>,
    Error,
    { pages: PageResponse<RepertoireEntry>[]; pageParams: number[] },
    ['repertoire', 'infinite', RepertoireInfiniteParams],
    number
  >
> {
  const { size = REPERTOIRE_PAGE_SIZE, search, queueStatusId, masteryLevelId, sort, direction } = params;
  return useInfiniteQuery({
    queryKey: ['repertoire', 'infinite', { size, search, queueStatusId, masteryLevelId, sort, direction }] as const,
    initialPageParam: 0,
    queryFn: ({ pageParam }) => {
      const searchParams = new URLSearchParams();
      searchParams.set('page', String(pageParam));
      searchParams.set('size', String(size));
      if (search) searchParams.set('search', search);
      if (queueStatusId) searchParams.set('queueStatusId', queueStatusId);
      if (masteryLevelId) searchParams.set('masteryLevelId', masteryLevelId);
      if (sort) searchParams.set('sort', sort);
      if (direction) searchParams.set('direction', direction);
      return api.fetch<PageResponse<RepertoireEntry>>(`/repertoire?${searchParams}`);
    },
    getNextPageParam: (lastPage) =>
      lastPage.page + 1 < lastPage.totalPages ? lastPage.page + 1 : undefined,
  });
}

export type AddToRepertoireRequest = {
  arrangementId: string;
  queueStatusId: string;
  masteryLevelId?: string;
  keyId?: string;
  practiceTempoBeats?: number;
  performanceTempoBeats?: number;
  notes?: string | null;
  firstPracticeOn?: string | null;
};

type UpdateRepertoireEntryRequest = {
  arrangementId: string;
  masteryLevelId?: string;
  queueStatusId?: string;
  isActive?: boolean;
  snoozedUntil?: string | null;
  keyId?: string | null;
  practiceTempoBeats?: number | null;
  performanceTempoBeats?: number | null;
  notes?: string | null;
  firstPracticeOn?: string | null;
};

export function useUpdateRepertoireEntry(): ReturnType<typeof useMutation<RepertoireEntry, Error, UpdateRepertoireEntryRequest>> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ arrangementId, ...data }: UpdateRepertoireEntryRequest) =>
      api.fetch<RepertoireEntry>(`/repertoire/${arrangementId}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['repertoire'] });
      void queryClient.invalidateQueries({ queryKey: ['practice-queue'] });
      void queryClient.invalidateQueries({ queryKey: ['repertoire', 'entry', variables.arrangementId] });
    },
  });
}

export function useRemoveFromRepertoire(): ReturnType<typeof useMutation<void, Error, string>> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (arrangementId: string) =>
      api.fetch<void>(`/repertoire/${arrangementId}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['repertoire'] });
      void queryClient.invalidateQueries({ queryKey: ['practice-queue'] });
    },
  });
}
